import type { FlagVerdictSource } from '@features/feature-flags';
import type { BadgeTone } from '@shared/ui';

import type { ComponentCardState, ComponentMemberFlag } from './component-card';
import type { ComponentSettingKind } from '../schemas/component-settings';

/**
 * Everything the client Components tab decides *about how a card looks* —
 * which tone a badge takes, which write a button issues, and how one checklist
 * row reads.
 *
 * A third module beside `component-card.ts` and `component-card-copy.ts`, on
 * the same grounds those two are apart from each other: one decides what is
 * true, one decides how to say it, and this one decides how to show it. The
 * reason all three are `.ts` rather than inline in the card is the workspace
 * rule — `apps/web` runs vitest on `node` with no jsdom, so a mapping written
 * inside a render is a mapping no test can read, and every mistake here renders
 * *something*, just the wrong thing.
 *
 * Kept out of `component-card-copy.ts` because these are not sentences. A tone
 * and a verb are decisions about the control; rewording a card must not be able
 * to change which write its button posts.
 */

/**
 * The badge tone for a card's state line.
 *
 * `not-activatable` is `neutral` rather than `danger` deliberately: a component
 * with no flags is an unfinished catalog row, not a fault in this museum's
 * account, and colouring it as a failure would send an operator looking for
 * something to fix on a client that has nothing wrong with it.
 *
 * `on-outstanding` is `warning` and not `success`, because the half of that
 * state worth acting on is the unfinished half — an operator scanning a column
 * of green badges will never stop on the one that still needs somebody.
 */
export function componentCardTone(state: ComponentCardState): BadgeTone {
    switch (state) {
        case 'not-activatable':
            return 'neutral';
        case 'on':
            return 'success';
        case 'on-outstanding':
            return 'warning';
        case 'off-provisioned':
            return 'info';
        case 'off-never-activated':
        case 'off-nothing-declared':
        case 'off-unknown':
            return 'neutral';
    }
}

/**
 * Which write the card's one button issues, or that there is none.
 *
 * Derived from the state rather than from the presence of
 * `ComponentCardCopy.action`, so the button's *label* and the button's *effect*
 * are decided by two independent functions over the same state. A card that
 * said "Activate" and posted a `DELETE` is the one failure mode this split
 * makes impossible, and it is invisible to every test in this workspace if the
 * branch lives in JSX.
 */
export type ComponentCardAction = 'activate' | 'deactivate' | 'none';

export function componentCardAction(state: ComponentCardState): ComponentCardAction {
    switch (state) {
        // No button at all — not a disabled one. There is nothing to grant, so
        // a control here would post a write that changes nothing.
        case 'not-activatable':
            return 'none';
        case 'on':
        case 'on-outstanding':
            return 'deactivate';
        case 'off-provisioned':
        case 'off-never-activated':
        case 'off-nothing-declared':
        case 'off-unknown':
            return 'activate';
    }
}

/* --------------------------------------------------------- the member row - */

/**
 * Why a flag resolves the way it does, in the words an operator uses.
 *
 * Total over `FlagVerdictSource` (`feature-flags/schemas/flag.ts:199`) and a
 * `Record` rather than a switch for it, so a sixth source added server-side is
 * a compile error here rather than a blank space on a card.
 *
 * `archived` and `rollout` are the two worth the space. Both can hold a flag
 * **off** while this museum's own override row says `true`, which is the case
 * an operator stares at a card about and cannot explain.
 */
export const MEMBER_SOURCE_LABEL: Record<FlagVerdictSource, string> = {
    override: 'this client’s own setting',
    default: 'the platform default',
    unset: 'no setting anywhere',
    archived: 'the flag being archived',
    rollout: 'a rollout it is outside of',
};

export interface MemberStateChip {
    readonly label: string;
    readonly tone: BadgeTone;
}

/**
 * One member flag's badge — the verdict, never the stored toggle.
 *
 * Three outcomes and not two. A member the museum's flags read never carried
 * gets its own, because rendering it as `Off` would report a capability as
 * withheld from this client when the truth is that the catalog names a flag the
 * response said nothing about — and the operator sent to this client's account
 * to fix it would find nothing wrong there.
 *
 * A member resolving off is `warning` rather than `danger` for the call
 * `componentCardTone` already makes about `not-activatable`: it is something to
 * look at, and colouring it as a failure of this client's account sends
 * somebody hunting on the wrong screen.
 */
export function memberStateChip(member: ComponentMemberFlag): MemberStateChip {
    if (!member.known) return { label: 'Not in this client’s flags', tone: 'neutral' };

    return member.enabled ? { label: 'On', tone: 'success' } : { label: 'Off', tone: 'warning' };
}

/* ------------------------------------------------------ the checklist row - */

/** How a setting gets done, in the words an operator uses for it. */
export const SETTING_KIND_LABEL: Record<ComponentSettingKind, string> = {
    manual: 'By hand',
    automatic: 'Reported by the service',
};

export function settingKindLabel(kind: ComponentSettingKind): string {
    return SETTING_KIND_LABEL[kind];
}

/**
 * The statuses this build has copy for, and how each one reads.
 *
 * A *hint*, exactly as the API's `KNOWN_SETTING_STATUSES` is a hint: the
 * backend writes these rows on its own release train and will ship a value this
 * bundle has never heard of. See {@link settingStatusChip} for what happens
 * then, which is the whole reason this map is allowed to be incomplete.
 */
const STATUS_COPY: Record<string, { label: string; tone: BadgeTone }> = {
    not_started: { label: 'Not started', tone: 'neutral' },
    in_progress: { label: 'In progress', tone: 'info' },
    done: { label: 'Done', tone: 'success' },
    blocked: { label: 'Blocked', tone: 'danger' },
};

export interface SettingStatusChip {
    readonly label: string;
    readonly tone: BadgeTone;
    /** Whether {@link label} is this build's wording or the backend's raw value. */
    readonly verbatim: boolean;
}

/**
 * One checklist row's status badge.
 *
 * A value with no copy is rendered **as itself**, uncoerced and unstyled as an
 * error. Three wrong answers were available here and all three are worse:
 * coercing it to `not_started` tells an operator that work nobody has looked at
 * is work nobody has started; rendering "Unknown" throws away the one fact the
 * backend took the trouble to send; and rendering it as a failure turns a
 * backend deploy that did nothing wrong into a screen full of red for every
 * museum holding that row.
 *
 * `known` is the API's own answer to "does this build have copy for it", but
 * the map is consulted too — the two can disagree by a release, and a `known`
 * value this bundle has no entry for must still come through rather than
 * render as `undefined`.
 */
export function settingStatusChip(row: {
    readonly status: string;
    readonly known: boolean;
}): SettingStatusChip {
    const copy = row.known ? STATUS_COPY[row.status] : undefined;

    if (!copy) return { label: row.status, tone: 'neutral', verbatim: true };

    return { label: copy.label, tone: copy.tone, verbatim: false };
}
